import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { reportPermissionsApi } from '../../api/reportPermissions.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Spinner } from '../../components/ui/index';
import { ROLE_LABELS } from '../../utils/roleHelpers';

const EXTRA_ROLES = [
  { key: 'manager_employee', label: 'مدیر کارمند' },
  { key: 'dispatcher', label: 'دیسپچر' },
  { key: 'simple_employee', label: 'کارمند' },
];

// tri-state per (employee, permission_key): no stored row = inherit the role
// default from the table above; true/false = an explicit override for that
// one person, in either direction.
function overrideValue(overrides, key) {
  if (!(key in overrides)) return 'inherit';
  return overrides[key] ? 'allow' : 'deny';
}

/** Admin-only: which roles (besides admin, which always sees every report)
 * can view each report - toggled per report_key against report_permissions.
 * Below that: per-employee overrides that win over the role defaults, for
 * both report visibility and cross-department ticket referral. */
export default function AccessControlPage() {
  const { addToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [permissionKeys, setPermissionKeys] = useState([]);
  const [savingCell, setSavingCell] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, employeesRes] = await Promise.all([
        reportPermissionsApi.getPermissions(),
        reportPermissionsApi.getEmployeePermissions(),
      ]);
      setReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
      setEmployees(Array.isArray(employeesRes.data?.employees) ? employeesRes.data.employees : []);
      setPermissionKeys(Array.isArray(employeesRes.data?.permission_keys) ? employeesRes.data.permission_keys : []);
    } catch {
      addToast('خطا در بارگذاری دسترسی‌ها', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (report, roleKey) => {
    const has = report.allowed_roles.includes(roleKey);
    const nextRoles = has
      ? report.allowed_roles.filter((r) => r !== roleKey)
      : [...report.allowed_roles, roleKey];
    setSaving(report.report_key);
    try {
      await reportPermissionsApi.savePermission(report.report_key, nextRoles);
      setReports((prev) => prev.map((r) => (r.report_key === report.report_key ? { ...r, allowed_roles: nextRoles } : r)));
    } catch {
      addToast('خطا در ذخیره دسترسی', 'error');
    }
    setSaving(null);
  };

  const setEmployeeOverride = async (employee, permissionKey, nextState) => {
    const granted = nextState === 'inherit' ? null : nextState === 'allow';
    const cellId = `${employee.employee_id}:${permissionKey}`;
    setSavingCell(cellId);
    try {
      const res = await reportPermissionsApi.saveEmployeePermission(employee.employee_id, permissionKey, granted);
      setEmployees(Array.isArray(res.data?.employees) ? res.data.employees : employees);
    } catch {
      addToast('خطا در ذخیره دسترسی اختصاصی', 'error');
    }
    setSavingCell(null);
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="کنترل دسترسی"
        subtitle="تعیین کنید علاوه بر ادمین (که همیشه به همه دسترسی دارد)، کدام نقش‌ها یا کدام کارمندان مشخص به هر گزارش و ارجاع بین‌دپارتمانی دسترسی دارند"
        action={<Link to="/settings" style={{ fontSize: 13, color: '#64748b' }}>بازگشت به تنظیمات</Link>}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '4px 0 10px' }}>دسترسی بر اساس نقش</div>
          {!reports.length ? (
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 24 }}>گزارشی یافت نشد.</div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflowX: 'auto', maxWidth: 760, marginBottom: 28 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ textAlign: 'right', padding: '10px 16px', color: '#64748b', fontWeight: 600 }}>گزارش</th>
                    {EXTRA_ROLES.map((role) => (
                      <th key={role.key} style={{ padding: '10px 16px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{role.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.report_key} style={{ borderTop: '1px solid #f1f5f9', opacity: saving === report.report_key ? 0.5 : 1 }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600, color: '#0f172a' }}>{report.label}</td>
                      {EXTRA_ROLES.map((role) => (
                        <td key={role.key} style={{ textAlign: 'center', padding: '10px 16px' }}>
                          <input
                            type="checkbox"
                            disabled={saving === report.report_key}
                            checked={report.allowed_roles.includes(role.key)}
                            onChange={() => toggle(report, role.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '4px 0 6px' }}>دسترسی اختصاصی هر کارمند</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
            برای هر کارمند می‌توانید مستقل از نقشش، یک گزارش یا ارجاع بین‌دپارتمانی را به‌طور اختصاصی مجاز یا غیرمجاز کنید — «پیش‌فرض نقش» یعنی بدون استثنا، طبق جدول بالا عمل شود.
          </div>
          {!employees.length ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>کارمندی یافت نشد.</div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflowX: 'auto', maxWidth: '100%' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, minWidth: 760 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ textAlign: 'right', padding: '10px 14px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>کارمند</th>
                    <th style={{ textAlign: 'right', padding: '10px 14px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>نقش</th>
                    {permissionKeys.map((pk) => (
                      <th key={pk.key} style={{ padding: '10px 12px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{pk.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => (
                    <tr key={employee.employee_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {employee.name || employee.username}
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{employee.department_name}</div>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#334155', whiteSpace: 'nowrap' }}>{ROLE_LABELS[employee.role] || employee.role}</td>
                      {permissionKeys.map((pk) => {
                        const cellId = `${employee.employee_id}:${pk.key}`;
                        return (
                          <td key={pk.key} style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <select
                              value={overrideValue(employee.overrides, pk.key)}
                              disabled={savingCell === cellId}
                              onChange={(e) => setEmployeeOverride(employee, pk.key, e.target.value)}
                              style={{ fontSize: 11.5, padding: '3px 4px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#334155', background: '#fff' }}
                            >
                              <option value="inherit">پیش‌فرض نقش</option>
                              <option value="allow">مجاز</option>
                              <option value="deny">غیرمجاز</option>
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
